import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import Button from '@/components/ui/Button';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    public static getDerivedStateFromError(error: Error): State {
        // Actualiza el state para mostrar la UI de error
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo,
        });

        // Aquí puedes enviar el error a un servicio de logging como Sentry
        // logErrorToService(error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    private handleGoHome = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            // Si se proporciona un fallback personalizado, usarlo
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // UI de error por defecto
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
                    <div className="max-w-md w-full text-center">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            {/* Icono de error */}
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                                <AlertTriangle className="h-8 w-8 text-red-600" />
                            </div>

                            {/* Título */}
                            <h1 className="text-2xl font-bold text-gray-900 mb-4">
                                ¡Oops! Algo salió mal
                            </h1>

                            {/* Descripción */}
                            <p className="text-gray-600 mb-6">
                                Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado
                                y está trabajando para solucionarlo.
                            </p>

                            {/* Acciones */}
                            <div className="space-y-3">
                                <Button
                                    onClick={this.handleReload}
                                    className="w-full"
                                    leftIcon={<RefreshCw className="w-4 h-4" />}
                                >
                                    Recargar página
                                </Button>

                                <Button
                                    onClick={this.handleGoHome}
                                    variant="outline"
                                    className="w-full"
                                    leftIcon={<Home className="w-4 h-4" />}
                                >
                                    Ir al inicio
                                </Button>
                            </div>

                            {/* Detalles del error (solo en desarrollo) */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="mt-6 text-left">
                                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                                        Detalles técnicos
                                    </summary>
                                    <div className="mt-2 p-4 bg-gray-100 rounded text-xs text-gray-700 overflow-auto max-h-40">
                                        <div className="font-medium mb-2">Error:</div>
                                        <div className="mb-4">{this.state.error.toString()}</div>

                                        {this.state.errorInfo && (
                                            <>
                                                <div className="font-medium mb-2">Stack Trace:</div>
                                                <pre className="whitespace-pre-wrap">
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </>
                                        )}
                                    </div>
                                </details>
                            )}

                            {/* Información de contacto */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <p className="text-sm text-gray-500">
                                    Si el problema persiste, contáctanos en{' '}
                                    <a
                                        href="mailto:soporte@Eisgemi.com"
                                        className="text-primary-600 hover:text-primary-500"
                                    >
                                        soporte@Eisgemi.com
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Hook para usar el error boundary en componentes funcionales
export const useErrorHandler = () => {
    return (error: Error, errorInfo?: ErrorInfo) => {
        console.error('Error caught by useErrorHandler:', error, errorInfo);

        // En una aplicación real, aquí enviarías el error a tu servicio de logging
        // logErrorToService(error, errorInfo);
    };
};

// Componente funcional de error boundary usando la nueva API
export const FunctionalErrorBoundary: React.FC<{
    children: ReactNode;
    fallback?: ReactNode;
    //onError?: (error: Error, errorInfo: ErrorInfo) => void;
}> = ({ children, fallback }) => {//,onError
    return (
        <ErrorBoundary fallback={fallback}>
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundary;